/**
 * Купон Polymarket: ссылка, в которой уже Market и сумма в долларах — или Limit,
 * цена строки и шары карточки, если человек выбрал лимитку.
 *
 * Ордер отсюда не уходит. Человек жмёт Buy на их сайте. Мы только собираем
 * адрес так, как их виджет его читает — иначе поле останется пустым, а клик
 * «открыть» обещает готовый купон.
 *
 * Площадка (бандл 6.09.2026, чанк `1jend6m0sj9cb.js`):
 * `price` — целое число тиков, `parseInt`. `0.42` станет нулём.
 * `shares` в Limit — штуки, не доллары. Доллар сюда класть нельзя.
 * `shares` в Market — ДОЛЛАРЫ покупки (бандл 02.10.2026: `getMarketOrderInfo`
 * сравнивает его с накопленной стоимостью asks). `price` Market не читает.
 *
 * Шары считает карточка: они зависят от банка человека. Общая ссылка ленты
 * их не знает и знать не должна.
 */

import { Decimal } from 'decimal.js';

import type { PolyOrder } from './market';

/** Поля ноги, без которых купон не собрать. Остальное `RowLeg` сюда не тащим. */
export interface CouponLeg {
  unit?: 'share' | 'money';
  lay: boolean;
  shares: number | null;
  cents: number | null;
  tick: number | null;
  minSize: number | null;
  /** Доллары покупки по рынку. Нет — ссылка Market откроется без суммы. */
  marketUsd?: number | null;
  /** Ключ рынка ноги: без него `/event` откроет победу матча. */
  marketKey?: string | null;
  side?: string | null;
}

/** Параметры, которые оверлей имеет право переписать. Исход и слаг — нет. */
const OVERWRITE = new Set(['orderType', 'side', 'shares', 'price']);

const POLY_LOCALES = new Set([
  'ru', 'en', 'es', 'fr', 'de', 'pt', 'it', 'pl', 'tr', 'uk', 'ko', 'ja', 'zh',
]);

/** Слаг события из `/event/{slug}` или двери `/sports|esports/…/{slug-дата}`. */
export function polyEventSlug(url: string): string | null {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    if (host !== 'polymarket.com' && !host.endsWith('.polymarket.com')) return null;
    const parts = parsed.pathname.split('/').filter(Boolean);
    if (parts[0] && POLY_LOCALES.has(parts[0].toLowerCase())) parts.shift();
    const last = parts.at(-1) ?? '';
    if (!last || last === 'undefined' || last.includes('/')) return null;
    if (parts[0] === 'event') return last;
    if ((parts[0] === 'esports' || parts[0] === 'sports') && /\d{4}-\d{2}-\d{2}/.test(last)) {
      return last;
    }
    return null;
  } catch {
    return null;
  }
}

function marketBits(key?: string | null): { kind: string; period: string; side: string } | null {
  if (!key) return null;
  const parts = key.split(':');
  if (parts.length !== 6) return null;
  return { kind: parts[0] ?? '', period: parts[2] ?? '', side: parts[3] ?? '' };
}

function outcomeSide(leg: CouponLeg): string {
  const fromKey = marketBits(leg.marketKey)?.side ?? '';
  const raw = fromKey || leg.side || '';
  if (raw === '1') return 'H';
  if (raw === '2') return 'A';
  return raw;
}

/**
 * Рынок и исход из ссылки + ключа ноги. Без этого `/event/{slug}` — победа
 * матча без `marketSlug`, и живой стакан даже не знает, какой токен спросить.
 */
export function polyPinnedMarket(
  url: string,
  marketKey?: string | null,
  side?: string | null,
): { slug: string; index: 0 | 1 } | null {
  try {
    const parsed = new URL(url);
    const fromQuery = parsed.searchParams.get('marketSlug');
    const rawIndex = parsed.searchParams.get('outcomeIndex');
    const event = polyEventSlug(url);
    const bits = marketBits(marketKey);
    const sideN = outcomeSide({
      lay: false, shares: null, cents: null, tick: null, minSize: null, marketKey, side,
    });
    const fromSide: 0 | 1 = sideN === 'A' ? 1 : 0;
    const index: 0 | 1 = rawIndex === '1' ? 1 : rawIndex === '0' ? 0 : fromSide;
    if (fromQuery) return { slug: fromQuery, index };
    if (!event) return null;
    if (bits?.kind === 'WIN' && /^P[1-9]\d*$/.test(bits.period)) {
      return { slug: `${event}-game${bits.period.slice(1)}`, index };
    }
    if (bits?.kind === 'WIN' && bits.period === 'M') {
      return { slug: event, index };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * На `/event/{slug}` без `marketSlug` виджет открывает победу матча.
 * Карта N — `{slug}-gameN`. Победа матча — сам слаг события. Фора без слага —
 * оставляем дверь, не матч.
 */
export function polyExactUrl(base: string, leg: CouponLeg = { lay: false, shares: null, cents: null, tick: null, minSize: null }): string {
  try {
    if (!isPolymarket(base)) return base;
    const parsed = new URL(base);
    const pin = polyPinnedMarket(base, leg.marketKey, leg.side);
    const event = polyEventSlug(base);
    if (pin) {
      if (event) parsed.pathname = `/event/${event}`;
      parsed.searchParams.set('marketSlug', pin.slug);
      if (!parsed.searchParams.has('outcomeIndex')) {
        parsed.searchParams.set('outcomeIndex', String(pin.index));
      }
      return parsed.toString();
    }
    return base;
  } catch {
    return base;
  }
}

/** Совместимость: без ноги не открываем `/event` — иначе карта станет матчем. */
export function polyTradeUrl(base: string, leg?: CouponLeg): string {
  return polyExactUrl(base, leg ?? { lay: false, shares: null, cents: null, tick: null, minSize: null });
}

/**
 * Цена строки → параметр `price`. `null` — считать не из чего или шаг врёт.
 *
 * Вниз к тику: дороже показанного не вбиваем. Тика нет — молчим: ошибка
 * на порядок стоит ордера по цене, которой мы не считали.
 */
export function priceTicks(cents: number, tick: number): string | null {
  if (!(cents > 0) || !(tick > 0)) return null;
  const dollars = new Decimal(cents).div(100);
  const step = new Decimal(tick);
  const ticks = dollars.div(step).toDecimalPlaces(0, Decimal.ROUND_DOWN);
  if (ticks.lte(0)) return null;
  const raw = ticks.toFixed(0);
  if (raw.includes('.')) return null;
  return raw;
}

/** Шары карточки строкой, которую площадка ест через `parseFloat`. */
export function sharesParam(shares: number): string | null {
  if (!(shares > 0) || !Number.isFinite(shares)) return null;
  return new Decimal(shares).toDecimalPlaces(4, Decimal.ROUND_DOWN).toString();
}

/**
 * Дописать Limit к адресу ноги. Чужой URL или нога «против» — вернуть как есть.
 *
 * `null` не возвращаем на живом адресе: потерять вкладку хуже, чем открыть
 * событие без суммы. Пустой `base` — `null`, как у вызывающего `urlOf`.
 */
export function limitUrl(base: string, leg: CouponLeg): string | null {
  if (base === '') return null;
  if (leg.lay || leg.unit === 'money' || !isPolymarket(base)) return base;
  base = polyExactUrl(base, leg);

  const params: Record<string, string> = {
    side: 'BUY',
    orderType: 'Limit',
  };
  if (leg.cents !== null && leg.tick !== null) {
    const price = priceTicks(leg.cents, leg.tick);
    if (price !== null) params.price = price;
  }
  if (leg.shares !== null) {
    const tooSmall = leg.minSize !== null && leg.shares < leg.minSize;
    const shares = tooSmall ? null : sharesParam(leg.shares);
    if (shares !== null) params.shares = shares;
  }

  try {
    return withParams(base, params);
  } catch {
    return base;
  }
}

/** Сумма Market строкой: вниз к центу — дороже посчитанного не тратим. */
export function amountParam(usd: number): string | null {
  if (!(usd > 0) || !Number.isFinite(usd)) return null;
  const cut = new Decimal(usd).toDecimalPlaces(2, Decimal.ROUND_DOWN);
  return cut.gt(0) ? cut.toFixed(2) : null;
}

/**
 * Дописать Market к адресу ноги: сторона, тип и сумма. Цену лимитки, если её
 * положила общая ссылка ленты, снимаем — в Market она ничего не значит, а
 * переключись человек на Limit, подставилась бы чужая цена.
 */
export function marketUrl(base: string, leg: CouponLeg): string | null {
  if (base === '') return null;
  if (leg.lay || leg.unit === 'money' || !isPolymarket(base)) return base;
  base = polyExactUrl(base, leg);
  const params: Record<string, string> = { side: 'BUY', orderType: 'Market' };
  const amount = leg.marketUsd == null ? null : amountParam(leg.marketUsd);
  if (amount !== null) params.shares = amount;
  try {
    const parsed = new URL(withParams(base, params));
    parsed.searchParams.delete('price');
    if (amount === null) parsed.searchParams.delete('shares');
    // На /event сверху график; якорь сажает кадр на книгу, как на sports.
    parsed.hash = 'order-book';
    return parsed.toString();
  } catch {
    return base;
  }
}

/** Купон по выбранному режиму. */
export function couponUrl(base: string, leg: CouponLeg, order: PolyOrder): string | null {
  return order === 'limit' ? limitUrl(base, leg) : marketUrl(base, leg);
}

function isPolymarket(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return host === 'polymarket.com' || host.endsWith('.polymarket.com');
  } catch {
    return false;
  }
}

export function isPolymarketUrl(url: string): boolean {
  return isPolymarket(url);
}

/**
 * Путь + рынок + исход. Сумма и Limit/Market не входят: это тот же купон.
 * `null` — не страница полика.
 */
export function polyPageKey(url: string): string | null {
  if (!isPolymarket(url)) return null;
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.replace(/\/+$/, '') || '/';
    const slug = (parsed.searchParams.get('marketSlug') ?? '').trim();
    const idx = (parsed.searchParams.get('outcomeIndex') ?? '').trim();
    return `${path}\n${slug}\n${idx}`;
  } catch {
    return null;
  }
}

export function samePolyPage(here: string, dest: string): boolean {
  const a = polyPageKey(here);
  const b = polyPageKey(dest);
  return a !== null && a === b;
}

/**
 * Уже открытая страница полика. Переписать её другим рынком — виджет
 * часто остаётся в Sell, и выигрышная поза пропадает.
 */
export function keepPolyTab(here: string | undefined, dest: string): boolean {
  if (here === undefined || here === '') return false;
  const from = polyPageKey(here);
  const to = polyPageKey(dest);
  return from !== null && to !== null && from !== to;
}

function withParams(url: string, params: Record<string, string>): string {
  const parsed = new URL(url);
  for (const [key, value] of Object.entries(params)) {
    if (OVERWRITE.has(key) || !parsed.searchParams.has(key)) {
      parsed.searchParams.set(key, value);
    }
  }
  return parsed.toString();
}
